import { CommonModule } from '@angular/common'
import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core'
import { HTVButtonControllerDirective } from '../../app-button-controller.directive'
import { PAGE_KEY } from '../../constants/page.constants'
import { LocalChannel } from '../../interfaces/local-channel'
import { ChannelPlayerComponent } from '../../layout/components/channel-player/channel-player.component'
import { ChannelProgressComponent } from '../../layout/components/channel-progress/channel-progress.component'
import { TracksMenuV3Component } from '../../layout/components/tracks-menu-v3/tracks-menu-v3.component'
import { ChannelPosition } from '../../pages/channel/channel-position'
import { ChannelService, RCU_PRESSED_DEBOUNCE } from '../../services/channel.service'
import { ConfigService } from '../../services/config.service'
import { EventService } from '../../services/event.service'
import { RouterService, RouterPage } from '../../services/router.service'
import { BaseWidgetComponent } from '../base-widget/base-widget.component'
import { debounceTime } from 'rxjs'

@Component({
  selector: 'app-channel-tv',
  standalone: true,
  imports: [CommonModule, ChannelPlayerComponent, ChannelProgressComponent, TracksMenuV3Component],
  templateUrl: './channel-tv.component.html',
  styleUrls: ['./channel-tv.component.scss'],
})
export class ChannelTVComponent extends BaseWidgetComponent implements OnInit, OnDestroy, AfterViewInit {
  channel?: LocalChannel

  isChannelPage!: boolean
  showProgressBar!: boolean

  overlayVisible = false
  trackMenuVisible = false
  useNativeTrackMenu = true
  hideOverlayTimeout: any

  @ViewChild('channelPlayer') channelPlayer!: ElementRef

  constructor(
    private appButtonDirective: HTVButtonControllerDirective,
    private cdr: ChangeDetectorRef,
    private channelService: ChannelService,
    private configService: ConfigService,
    private eventService: EventService,
    private routerService: RouterService
  ) {
    super()
  }

  override ngOnInit(): void {
    super.ngOnInit()

    // Use current page to know if we're on channel page or another (to handle overlay etc)
    this.isChannelPage = this.routerService.isCurrentPage(RouterPage.CHANNEL)
    this.showProgressBar = !this.isChannelPage

    this.subscriptions.add(
      this.pageService.getSubject(PAGE_KEY.TRACK_MENU_OVERRIDE)?.subscribe((hasOverride: boolean | undefined) => {
        if (hasOverride !== undefined) {
          this.useNativeTrackMenu = !hasOverride
        } else {
          this.useNativeTrackMenu = true
        }
      })
    )
  }

  ngAfterViewInit(): void {
    this.registerChannelListener()
    if (this.isChannelPage) {
      this.registerEventListener()
    }
    this.selectPendingChannel()
  }

  override ngOnDestroy(): void {
    if (this.hideOverlayTimeout) {
      clearTimeout(this.hideOverlayTimeout)
    }
    this.channelService.stopChannel()
    this.subscriptions.unsubscribe()

    super.ngOnDestroy()
  }

  registerEventListener(): void {
    this.subscriptions.add(
      this.eventService.keyDown.subscribe((key: string) => {
        switch (key) {
          case 'BACK':
            if (this.trackMenuVisible) {
              this.hideTrackMenu()
              return
            }
            if (this.overlayVisible) {
              this.hideOverlay()
              return
            }
            this.routerService.goBack()
            break
          case 'COL-BLUE':
          case 'SUBTITLE':
            if (this.useNativeTrackMenu) {
              this.toggleTrackMenu()
            } else {
              this.showOverlay()
            }
            break
          default:
            if (this.trackMenuVisible) {
              return
            }
            this.showOverlay()
            break
        }
      })
    )
  }

  selectPendingChannel(): void {
    if (this.channelService.selectedChannel || this.channelService.channels.length === 0) {
      return
    }
    this.channelService.selectChannel(this.channelService.getPendingChannel())
  }

  registerChannelListener(): void {
    this.subscriptions.add(
      this.channelService.selectedChannel$
        .pipe(debounceTime(RCU_PRESSED_DEBOUNCE))
        .subscribe((channel: LocalChannel | undefined) => {
          if (channel && this.channel != channel) {
            this.loadChannel(channel)
            this.cdr.detectChanges()

            // TODO: need to handle it ?
            //this.appButtonDirective.forceRefreshSelectedButton()
          }
        })
    )
  }

  loadChannel(channel: LocalChannel): void {
    this.channel = channel
    const boundingRect: DOMRect = this.channelPlayer.nativeElement.getBoundingClientRect()
    const pos: ChannelPosition = {
      left: boundingRect.left,
      top: boundingRect.top,
      width: boundingRect.width,
      height: boundingRect.height,
    }
    this.pageService.updateSubject(PAGE_KEY.SELECTED_CHANNEL, channel)
    this.channelService.startChannel(this.channel, pos)
    this.showOverlay()
  }

  toggleTrackMenu(): void {
    if (!this.isChannelPage || !this.useNativeTrackMenu) {
      return
    }
    this.trackMenuVisible = !this.trackMenuVisible
  }

  showTrackMenu(): void {
    if (!this.isChannelPage || !this.useNativeTrackMenu) {
      return
    }
    this.trackMenuVisible = true
  }

  hideTrackMenu(): void {
    if (!this.isChannelPage || !this.useNativeTrackMenu) {
      return
    }
    this.trackMenuVisible = false
  }

  showOverlay(): void {
    if (!this.isChannelPage) {
      return
    }

    this.overlayVisible = true
    this.pageService.updateSubject(PAGE_KEY.OVERLAY_VISIBILITY, true)

    this.registerOverlayTimeout()
  }

  hideOverlay(): void {
    if (!this.isChannelPage) {
      return
    }

    if (this.trackMenuVisible) {
      this.registerOverlayTimeout()
      return
    }

    this.appButtonDirective.clearSelectedButtons()
    this.overlayVisible = false
    this.pageService.updateSubject(PAGE_KEY.OVERLAY_VISIBILITY, false)
  }

  registerOverlayTimeout(): void {
    if (this.hideOverlayTimeout) {
      clearTimeout(this.hideOverlayTimeout)
    }
    this.hideOverlayTimeout = setTimeout(() => {
      this.hideOverlay()
    }, this.configService.channelOverlayDurationInMilliseconds)
  }
}
